import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios, { API_URL } from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import '../pages/Profiles.css';

const DetalleTramiteModal = ({ isOpen, onClose, tramiteId, onUpdate }) => {
  const { user } = useContext(AuthContext);
  const [tramite, setTramite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchTramiteDetalle = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/tramites/${tramiteId}`);
      setTramite(res.data);
      setObservaciones(res.data.observaciones_validador || '');
    } catch (err) {
      console.error('Error al cargar detalle del trámite:', err);
    } finally {
      setLoading(false);
    }
  }, [tramiteId]);

  useEffect(() => {
    if (isOpen && tramiteId) {
      fetchTramiteDetalle();
    }
  }, [isOpen, tramiteId, fetchTramiteDetalle]);

  // eslint-disable-next-line no-unused-vars
  const handleAction = async (nuevoEstado) => {
    try {
      setUpdating(true);
      await axios.put(`/api/tramites/${tramiteId}/estado`, {
        estado: nuevoEstado,
        observaciones: observaciones
      });
      onUpdate();
      onClose();
    } catch (err) {
      alert('Error al actualizar el trámite');
    } finally {
      setUpdating(false);
    }
  };

  const checklistItems = tramite ? [
    { 
      key: 'radicado', 
      label: 'Radicado', 
      icon: '📝',
      done: true, // Siempre completado si existe el trámite
      detail: `Fecha: ${new Date(tramite.created_at).toLocaleDateString('es-CO')}`
    },
    { 
      key: 'placa', 
      label: 'Placa Pre-asignada', 
      icon: '🚗',
      done: !!tramite.pre_assigned_plate_id && tramite.estado !== 'radicado',
      detail: tramite.datos_adicionales?.placa_referencia || null
    },
    { 
      key: 'soat', 
      label: 'SOAT', 
      icon: '🛡️',
      done: tramite.datos_adicionales?.soat_cargado === true,
      approved: tramite.datos_adicionales?.soat_aprobado === true,
      detail: tramite.datos_adicionales?.soat_info?.numero 
        ? `N° ${tramite.datos_adicionales.soat_info.numero}` 
        : null
    },
    { 
      key: 'cupl', 
      label: 'CUPL', 
      icon: '📄',
      done: tramite.datos_adicionales?.cupl_cargado === true,
      paid: tramite.datos_adicionales?.cupl_pagado === true,
      detail: tramite.datos_adicionales?.cupl_number 
        ? `N° ${tramite.datos_adicionales.cupl_number}${tramite.datos_adicionales.cupl_fecha_vencimiento ? ' • Vence: ' + tramite.datos_adicionales.cupl_fecha_vencimiento : ''}` 
        : null
    },
    { 
      key: 'ejecutado', 
      label: 'Ejecutado', 
      icon: '🔒',
      done: tramite.estado === 'tramite_ejecutado',
      detail: null
    }
  ] : [];

  const getStatusInfo = (status) => {
    const statuses = {
      'radicado': { label: 'Radicado', class: 'pending' },
      'pre_asignado': { label: 'Pre-Asignado', class: 'activo' },
      'novedad_pendiente_soat': { label: 'SOAT Pendiente', class: 'warning' },
      'novedad_soat_cargado': { label: 'SOAT Cargado', class: 'warning' },
      'novedad_soat_vigente': { label: 'SOAT Vigente', class: 'paid' },
      'novedad_pendiente_cupl': { label: 'CUPL Pendiente', class: 'warning' },
      'novedad_cupl_generado': { label: 'CUPL Generado', class: 'warning' },
      'novedad_cupl_pagado': { label: 'CUPL Pagado', class: 'paid' },
      'tramite_ejecutado': { label: 'Trámite Ejecutado', class: 'paid' },
      'rechazado': { label: 'Rechazado', class: 'expired' }
    };
    return statuses[status] || { label: status, class: '' };
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '900px', width: '95%', maxHeight: '95vh', overflowY: 'auto', padding: '0' }}>
        {/* Header con color de fondo suave */}
        <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', position: 'sticky', top: '0', zIndex: '10' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h3 style={{ margin: '0' }}>⚖️ Detalle de Trámite #{tramiteId}</h3>
            {tramite && (
              <span className={`status ${getStatusInfo(tramite.estado).class}`} style={{ fontSize: '12px', padding: '4px 12px' }}>
                {getStatusInfo(tramite.estado).label}
              </span>
            )}
          </div>
          <button className="close-btn" onClick={onClose} style={{ fontSize: '24px', cursor: 'pointer', background: 'none', border: 'none', color: '#64748b' }}>&times;</button>
        </div>

        <div style={{ padding: '30px' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="loading-spinner"></div>
              <p>Cargando información del trámite...</p>
            </div>
          ) : tramite ? (
            <>
              {/* Banner de Rechazo */}
              {tramite.estado === 'rechazado' && (
                <div className="error-banner" style={{ marginBottom: '30px', borderRadius: '12px', padding: '20px', background: '#fef2f2', border: '1px solid #fee2e2', color: '#991b1b' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '20px' }}>❌</span>
                    <strong style={{ fontSize: '16px' }}>Trámite Rechazado</strong>
                  </div>
                  {tramite.observaciones_validador ? (
                    <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                      <strong>Motivo:</strong> {tramite.observaciones_validador}
                    </p>
                  ) : (
                    <p style={{ margin: 0, fontSize: '14px', fontStyle: 'italic' }}>No se proporcionaron observaciones adicionales.</p>
                  )}
                </div>
              )}

              {/* Checklist de Progreso */}
              <div style={{ marginBottom: '40px', background: '#f8fafc', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b', fontSize: '16px' }}>
                  ✅ Progreso del Trámite
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
                    ({checklistItems.filter(i => i.done).length}/{checklistItems.length} completados)
                  </span>
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {checklistItems.map((item) => (
                    <div 
                      key={item.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        padding: '14px 18px',
                        borderRadius: '12px',
                        background: item.done ? 'white' : 'rgba(255,255,255,0.5)',
                        border: item.done ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        transition: 'all 0.2s',
                        opacity: item.done ? 1 : 0.7
                      }}
                    >
                      {/* Checkbox visual */}
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0,
                        background: item.done ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#f1f5f9',
                        color: item.done ? 'white' : '#94a3b8',
                        border: item.done ? 'none' : '2px solid #cbd5e1',
                        boxShadow: item.done ? '0 2px 6px rgba(34,197,94,0.3)' : 'none'
                      }}>
                        {item.done ? '✓' : ''}
                      </div>

                      {/* Icono y nombre */}
                      <span style={{ fontSize: '18px' }}>{item.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: '600', 
                          fontSize: '14px', 
                          color: item.done ? '#1e293b' : '#64748b',
                          textDecoration: item.done ? 'none' : 'none'
                        }}>
                          {item.label}
                          {/* Sub-badges para SOAT y CUPL */}
                          {item.key === 'soat' && item.done && (
                            <span style={{
                              marginLeft: '8px',
                              fontSize: '10px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: '700',
                              background: item.approved ? '#dcfce7' : '#fef3c7',
                              color: item.approved ? '#16a34a' : '#d97706'
                            }}>
                              {item.approved ? 'APROBADO' : 'PENDIENTE APROBACIÓN'}
                            </span>
                          )}
                          {item.key === 'cupl' && item.done && (
                            <span style={{
                              marginLeft: '8px',
                              fontSize: '10px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: '700',
                              background: item.paid ? '#dcfce7' : '#fef3c7',
                              color: item.paid ? '#16a34a' : '#d97706'
                            }}>
                              {item.paid ? 'PAGADO' : 'PENDIENTE PAGO'}
                            </span>
                          )}
                        </div>
                        {item.detail && (
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            {item.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="detalle-tramite-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                {/* Columna Izquierda: Datos */}
                <div className="detalle-info">
                  <div className="info-section">
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#1e293b' }}>
                      📋 Datos Principales
                    </h4>
                    <div className="info-card" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <p style={{ margin: 0 }}><strong>Tipo:</strong> {tramite.tramite_type_name}</p>
                        <p style={{ margin: 0 }}><strong>Tránsito:</strong> {tramite.transito_name}</p>
                        <p style={{ margin: 0 }}>
                          <strong>Propietario:</strong> {tramite.datos_adicionales?.propietario}
                          {tramite.datos_adicionales?.propietario_original && 
                           tramite.datos_adicionales.propietario_original.trim().toUpperCase() !== tramite.datos_adicionales.propietario.trim().toUpperCase() && (
                            <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px', fontStyle: 'italic' }}>
                              (Inició como: {tramite.datos_adicionales.propietario_original})
                            </span>
                          )}
                        </p>
                        <p style={{ margin: 0 }}><strong>Placa Ref:</strong> <span className="plate-badge-sm">{tramite.datos_adicionales?.placa_referencia || 'N/A'}</span></p>
                        <p style={{ margin: 0 }}><strong>Creado por:</strong> {tramite.creador_name}</p>
                        <p style={{ margin: 0 }}><strong>Fecha Radicación:</strong> {new Date(tramite.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Sección de Detalles del Vehículo */}
                  {tramite.datos_adicionales?.vehicleDetails?.vehiculo && (
                    <div className="info-section" style={{ marginTop: '25px' }}>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#1e293b' }}>
                        🚗 Detalles del Vehículo
                      </h4>
                      <div className="info-card" style={{ background: '#f0fdf4', padding: '20px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.placa && (
                            <p style={{ margin: 0 }}><strong>Placa:</strong> <span className="plate-badge-sm">{tramite.datos_adicionales.vehicleDetails.vehiculo.placa}</span></p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.marca && (
                            <p style={{ margin: 0 }}><strong>Marca:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.marca}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.linea && (
                            <p style={{ margin: 0 }}><strong>Línea:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.linea}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.modelo && (
                            <p style={{ margin: 0 }}><strong>Modelo:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.modelo}</p>
                          )}
                          {((tramite.datos_adicionales.vehicleDetails.vehiculo.cilindraje !== undefined && tramite.datos_adicionales.vehicleDetails.vehiculo.cilindraje !== null) || 
                            (tramite.datos_adicionales.vehicleDetails.vehiculo.datos_completos?.rna?.datos?.cilindraje !== undefined && tramite.datos_adicionales.vehicleDetails.vehiculo.datos_completos?.rna?.datos?.cilindraje !== null)) && (
                            <p style={{ margin: 0 }}>
                              <strong>Cilindraje:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.cilindraje || tramite.datos_adicionales.vehicleDetails.vehiculo.datos_completos.rna.datos.cilindraje} cc
                            </p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.color && (
                            <p style={{ margin: 0 }}><strong>Color:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.color}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.nro_motor && (
                            <p style={{ margin: 0 }}><strong>N° Motor:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.nro_motor}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.nro_chasis && (
                            <p style={{ margin: 0 }}><strong>N° Chasis:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.nro_chasis}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.nro_vin && (
                            <p style={{ margin: 0 }}><strong>VIN:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.nro_vin}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.servicio && (
                            <p style={{ margin: 0 }}><strong>Servicio:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.servicio}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.clase && (
                            <p style={{ margin: 0 }}><strong>Clase:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.clase}</p>
                          )}
                          {tramite.datos_adicionales.vehicleDetails.vehiculo.estado_vehiculo && (
                            <p style={{ margin: 0 }}><strong>Estado:</strong> {tramite.datos_adicionales.vehicleDetails.vehiculo.estado_vehiculo}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {(user?.role === 'superadmin' || user?.role === 'admin') && tramite.estado === 'radicado' && (
                    <div className="info-section" style={{ marginTop: '25px' }}>
                      <h4 style={{ marginBottom: '10px' }}>✍️ Observaciones de Validación</h4>
                      <textarea
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        placeholder="Escriba aquí los motivos del rechazo o notas para el trámite..."
                        rows="4"
                        style={{ width: '100%', padding: '15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', resize: 'none' }}
                      />
                    </div>
                  )}
                </div>

                {/* Columna Derecha: Documentos */}
                <div className="detalle-docs">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#1e293b' }}>
                    📂 Documentación Cargada
                  </h4>
                  <div className="docs-list" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {tramite.archivos && tramite.archivos.length > 0 ? (
                      tramite.archivos.map(archivo => (
                        <div key={archivo.id} className="doc-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                          <div style={{ flex: 1, marginRight: '15px', overflow: 'hidden' }}>
                            <span style={{ fontSize: '10px', fontWeight: '800', color: '#2563eb', display: 'block', textTransform: 'uppercase', marginBottom: '4px' }}>
                              {archivo.tipo_documento.replace(/_/g, ' ')}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#334155', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={archivo.nombre_mostrar}>
                              {archivo.nombre_mostrar}
                            </span>
                          </div>
                          <a 
                            href={`${API_URL}/uploads/tramites/${archivo.nombre_disco}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-view-doc"
                            style={{ 
                              padding: '8px 16px', 
                              fontSize: '13px', 
                              textDecoration: 'none',
                              background: '#f1f5f9',
                              color: '#475569',
                              borderRadius: '8px',
                              fontWeight: '700',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            👁️ Ver
                          </a>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: '30px', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0' }}>
                        <p style={{ color: '#64748b', margin: 0, fontStyle: 'italic' }}>No se han cargado documentos para este trámite.</p>
                      </div>
                    )}
                  </div>

                  {/* Historial de Estados */}
                  {tramite.estado_historial && tramite.estado_historial.length > 0 && (
                    <div style={{ marginTop: '25px' }}>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#1e293b' }}>
                        🕐 Historial de Estados
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0', position: 'relative' }}>
                        {tramite.estado_historial.map((h, idx) => (
                          <div key={h.id} style={{ display: 'flex', gap: '12px', position: 'relative' }}>
                            {/* Línea vertical */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '20px' }}>
                              <div style={{ 
                                width: '12px', height: '12px', borderRadius: '50%', 
                                background: idx === tramite.estado_historial.length - 1 ? '#2563eb' : '#94a3b8',
                                border: idx === tramite.estado_historial.length - 1 ? '3px solid #bfdbfe' : '2px solid #cbd5e1',
                                zIndex: 1, flexShrink: 0
                              }} />
                              {idx < tramite.estado_historial.length - 1 && (
                                <div style={{ width: '2px', background: '#e2e8f0', flexGrow: 1, minHeight: '30px' }} />
                              )}
                            </div>
                            {/* Contenido */}
                            <div style={{ paddingBottom: '16px', flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                {h.estado_anterior && (
                                  <>
                                    <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: '#f1f5f9', color: '#64748b', fontWeight: '600' }}>
                                      {getStatusInfo(h.estado_anterior).label}
                                    </span>
                                    <span style={{ color: '#94a3b8', fontSize: '12px' }}>→</span>
                                  </>
                                )}
                                <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af', fontWeight: '600' }}>
                                  {getStatusInfo(h.estado_nuevo).label}
                                </span>
                              </div>
                              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                                {new Date(h.created_at).toLocaleString('es-CO')} — <span style={{ fontWeight: '600' }}>{h.usuario_nombre || 'Sistema'}</span>
                              </p>
                              {h.observaciones && (
                                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                                  {h.observaciones}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>No se pudo cargar la información del trámite.</p>
            </div>
          )}

          {/* Acciones del Modal */}
          <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '30px', marginTop: '40px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
            <button type="button" onClick={onClose} className="btn-secondary" disabled={updating} style={{ padding: '10px 25px', borderRadius: '10px', fontWeight: '600' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalleTramiteModal;
